// VoidNoticePreview — the "nothing to sign" email, shown before a void.
//
// CONSUMER: internal-ui.
//
// Baker, 2026-10-05: he cancelled a stale package and the OS emailed the
// client "I've withdrawn … there's nothing for you to sign," from his own
// Outlook, and he did not know it had until the client wrote back confused.
// The dialogs said "they get an email" in one line but never showed it and
// never let him choose. This shows the exact email each person gets, using
// the reason typed so far, and offers a tick box to void without emailing.
//
// DECISION: the preview comes from the server (GET …/void-preview), which
// builds it with the same function that sends it. A copy of the template in
// the browser would drift the first time someone edits the wording in
// Config. The text is NOT editable here: the reason field already feeds
// the email, and free text per void would let wording drift from the
// firm-wide template. Revisit if people ask.

import { useEffect, useState } from "react";

import { apiV11 } from "../../api";

interface Props {
  envelopeId: string;
  /** The reason as typed so far; the preview re-renders as it changes. */
  reason: string;
  /** Ticked = the signers are emailed. Default is ticked (the old behaviour). */
  notify: boolean;
  onNotifyChange: (notify: boolean) => void;
}

type Notice = { name: string; email: string; subject: string; body: string };

export function VoidNoticePreview({ envelopeId, reason, notify, onNotifyChange }: Props) {
  const [notices, setNotices] = useState<Notice[] | null>(null);
  const [failed, setFailed] = useState(false);

  // Debounced so typing a sentence is not one request per keystroke.
  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    const timer = window.setTimeout(() => {
      apiV11
        .previewEsignVoidNotice(envelopeId, reason)
        .then((res) => {
          if (!cancelled) setNotices(res.notices);
        })
        .catch(() => {
          if (!cancelled) setFailed(true);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [envelopeId, reason]);

  if (failed) {
    // Never block a void because the preview failed, but never pretend the
    // email isn't going out either.
    return (
      <div className="doc-void-notice" data-testid="void-notice">
        <p className="doc-void-notice-note">
          Couldn't load a preview of the email to the signers. If you leave the box below ticked, they will
          still be emailed that it was withdrawn.
        </p>
        <NotifyBox notify={notify} onNotifyChange={onNotifyChange} />
      </div>
    );
  }

  if (notices === null) {
    return (
      <div className="doc-void-notice" data-testid="void-notice">
        <p className="doc-void-notice-note">Loading the email the signers would get…</p>
      </div>
    );
  }

  if (notices.length === 0) {
    return (
      <div className="doc-void-notice" data-testid="void-notice">
        <p className="doc-void-notice-note">
          Nobody has been asked to sign yet, so no email goes out.
        </p>
      </div>
    );
  }

  return (
    <div className="doc-void-notice" data-testid="void-notice">
      <NotifyBox notify={notify} onNotifyChange={onNotifyChange} />
      {notify ? (
        <>
          <p className="doc-void-notice-note">
            Sent from your own Outlook, so it reads as a note from you. This is exactly what{" "}
            {notices.length === 1 ? "they'll" : "each of them will"} see:
          </p>
          {notices.map((n) => (
            <div className="doc-void-email" key={n.email}>
              <div className="doc-void-email-to">
                To {n.name} &lt;{n.email}&gt;
              </div>
              <div className="doc-void-email-subject">{n.subject}</div>
              <div className="doc-void-email-body">{n.body}</div>
            </div>
          ))}
        </>
      ) : (
        <p className="doc-void-notice-note">
          Nobody will be emailed. The signing links still stop working, so anyone who opens their old email
          will see "this link isn't valid." Let them know yourself.
        </p>
      )}
    </div>
  );
}

function NotifyBox({
  notify,
  onNotifyChange,
}: {
  notify: boolean;
  onNotifyChange: (notify: boolean) => void;
}) {
  return (
    <label className="doc-void-notify">
      <input type="checkbox" checked={notify} onChange={(e) => onNotifyChange(e.target.checked)} />
      <span>Email the signers that this was withdrawn</span>
    </label>
  );
}
